import type { OutcomePageContent } from "./types";

/**
 * Industry pages. Same shape as the solutions, aimed at a buyer who arrives
 * with a regulator rather than a product in mind.
 *
 * The `drivers` blocks name real instruments with their real citations. Where
 * a sector expects an authorization Odingard does not hold — FedRAMP being the
 * obvious one — the page says so rather than leaving the impression open.
 */

const FINANCIAL_SERVICES: OutcomePageContent = {
  path: "/industry/financial-services",
  kind: "industry",
  eyebrow: "Industry",
  headline:
    "Model risk governance already had a rulebook — agentic AI has to be brought inside it",
  lede:
    "Financial institutions were governing models before the current wave of AI, and examiners will expect the same discipline applied to systems that act autonomously.",
  ctas: [
    { label: "Request a briefing", href: "/contact?topic=financial-services" },
    { label: "Explore TraceLock", href: "/tracelock" },
  ],
  challenge: {
    heading: "The controls assume a human in the loop",
    body:
      "Existing model risk frameworks were written for models that produce an output someone reviews. An agent that reads a customer record and then acts on it does not fit the validation pattern, and the examiner will still ask how it is controlled.",
    points: [
      {
        title: "Autonomy outruns validation",
        body: "Periodic model validation cannot describe a system whose behavior depends on what it read this morning.",
      },
      {
        title: "Customer data is in the context window",
        body: "Agents with legitimate access to account data are exactly the systems an injection attack targets.",
      },
      {
        title: "Disclosure has a clock on it",
        body: "Material incidents have to be described accurately and quickly, which requires a record that was being kept before the incident.",
      },
    ],
  },
  outcomes: {
    heading: "With Odingard, you can",
    items: [
      {
        title: "Constrain what agents reach",
        body: "Cerberus interrupts the outbound action when sensitive access, untrusted content and an exfiltration path converge — at the tool call, not the prompt.",
      },
      {
        title: "Bring AI into the model inventory",
        body: "TraceLock's AI inventory tracks models and AI services with the obligations attached, so governance covers the estate rather than the documented part of it.",
      },
      {
        title: "Keep control evidence current",
        body: "Connectors test the live environment on a schedule, and artifacts are recorded write-once with a SHA-256 digest.",
      },
    ],
  },
  drivers: {
    heading: "What examiners and regulators are working from",
    items: [
      {
        title: "SEC cybersecurity disclosure rules",
        body: "Material incidents are reported on Form 8-K Item 1.05, with annual risk-management disclosure under Regulation S-K Item 106.",
      },
      {
        title: "SR 11-7 model risk management",
        body: "The supervisory guidance on model risk remains the reference point examiners use when AI is in a decision path.",
      },
      {
        title: "GLBA Safeguards Rule",
        body: "A documented information security program, with the vendor oversight that goes with it.",
      },
      {
        title: "DORA (Regulation (EU) 2022/2554)",
        body: "Operational resilience and ICT third-party risk obligations for EU financial entities.",
      },
    ],
  },
  products: {
    heading: "What delivers it",
    slugs: ["cerberus", "tracelock"],
  },
  services: {
    heading: "Bring in Warden",
    body:
      "Warden assesses agent readiness against your model risk framework, builds the crosswalk to your control set, and briefs the board.",
    links: [
      { label: "Model Risk Governance", href: "/warden/model-risk" },
      { label: "Agent Readiness Assessment", href: "/warden/agent-readiness" },
      { label: "Board & Regulator Briefings", href: "/warden/briefings" },
    ],
  },
  resources: {
    heading: "Go deeper",
    items: [
      { kind: "Solution", label: "Govern AI Adoption", href: "/govern-ai" },
      { kind: "Solution", label: "Manage Third-Party Risk", href: "/third-party-risk" },
      { kind: "Product", label: "TraceLock", href: "/tracelock" },
    ],
  },
  close: {
    heading: "Govern agents the way you already govern models",
    body:
      "The framework exists. The work is extending it to systems that act without waiting for review.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=financial-services" },
      { label: "Explore Warden", href: "/warden" },
    ],
  },
};

const HEALTHCARE: OutcomePageContent = {
  path: "/industry/healthcare",
  kind: "industry",
  eyebrow: "Industry",
  headline: "An agent with access to PHI is a disclosure risk that never sleeps",
  lede:
    "Clinical and administrative AI needs the record to do anything useful. The control that matters is what leaves — and whether you can prove it did not.",
  ctas: [
    { label: "Request a briefing", href: "/contact?topic=healthcare" },
    { label: "Explore Cerberus", href: "/cerberus" },
  ],
  challenge: {
    heading: "Minimum necessary is hard to enforce on a system that reasons",
    body:
      "An agent summarizing a chart legitimately reads PHI. Whether that PHI then reaches a destination it should not is a runtime question, and access control answered it before the request was made.",
    points: [
      {
        title: "Legitimate access, illegitimate destination",
        body: "The breach path is not the read. It is the outbound call that follows content the agent was told to trust.",
      },
      {
        title: "Business associates multiply the surface",
        body: "Every vendor adding an AI feature changes where PHI can travel, under an agreement signed before the feature existed.",
      },
      {
        title: "Breach notification needs facts",
        body: "Determining what was disclosed requires a record of what the system actually did, not an inference from logs.",
      },
    ],
  },
  outcomes: {
    heading: "With Odingard, you can",
    items: [
      {
        title: "Stop the exfiltration path",
        body: "Cerberus holds the guarded outbound call when sensitive access and untrusted content converge, so the byte does not leave.",
      },
      {
        title: "Reconstruct what happened",
        body: "Verdicts carry the signals and contamination graph behind them, which is what an incident determination actually needs.",
      },
      {
        title: "Keep Security Rule evidence live",
        body: "TraceLock crosswalks the HIPAA Security Rule alongside SOC 2 and ISO 27001, tested continuously rather than annually.",
      },
    ],
  },
  drivers: {
    heading: "The obligations in play",
    items: [
      {
        title: "HIPAA Security Rule (45 CFR Part 164, Subpart C)",
        body: "Administrative, physical and technical safeguards for electronic PHI, with documentation that has to be maintained.",
      },
      {
        title: "HIPAA Breach Notification Rule",
        body: "Notification timetables that depend on being able to determine the scope of a disclosure.",
      },
      {
        title: "FDA guidance on AI-enabled device software",
        body: "Where AI is part of a regulated device, lifecycle and change management expectations apply on top of security.",
      },
    ],
  },
  products: {
    heading: "What delivers it",
    slugs: ["cerberus", "tracelock"],
  },
  services: {
    heading: "Bring in Warden",
    body:
      "Warden reviews the tool boundaries around clinical and administrative agents, then operates the evidence program behind them.",
    links: [
      { label: "Tool-Boundary Review", href: "/warden/tool-boundary-review" },
      { label: "Managed Compliance", href: "/warden/managed-compliance" },
      { label: "AI Inventory & Discovery", href: "/warden/ai-inventory" },
    ],
  },
  resources: {
    heading: "Go deeper",
    items: [
      { kind: "Solution", label: "Secure Agentic AI", href: "/secure-agentic-ai" },
      { kind: "Solution", label: "Stay Continuously Audit-Ready", href: "/audit-ready" },
      { kind: "Research", label: "Lethal Trifecta validation report", href: "/research" },
    ],
  },
  close: {
    heading: "Let clinical AI use the record safely",
    body:
      "The useful version of healthcare AI needs real access. Constrain the action instead of withholding the data.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=healthcare" },
      { label: "Explore Cerberus", href: "/cerberus" },
    ],
  },
};

const MANUFACTURING: OutcomePageContent = {
  path: "/industry/manufacturing",
  kind: "industry",
  eyebrow: "Industry",
  headline:
    "AI is reaching into the plant floor through the IT side, where the OT controls do not apply",
  lede:
    "Production data, supplier terms and process knowledge are now inside AI systems that were never scoped as operational technology.",
  ctas: [
    { label: "Request a briefing", href: "/contact?topic=manufacturing" },
    { label: "Explore Cerberus", href: "/cerberus" },
  ],
  challenge: {
    heading: "The crown jewels are process knowledge, and they are text",
    body:
      "Specifications, tolerances, yields and supplier pricing are exactly the material an agent is most useful summarizing — and exactly what a competitor or an export-control regime cares about.",
    points: [
      {
        title: "Segmentation does not cover the copy",
        body: "Air-gapping the line does nothing about the process document an assistant just read and forwarded.",
      },
      {
        title: "Supplier obligations are unread",
        body: "Quality, indemnity and notification terms sit in agreements nobody reviews until a dispute.",
      },
      {
        title: "Defense supply chains inherit requirements",
        body: "A tier-two supplier picks up the prime's security obligations through the flow-down, AI included.",
      },
    ],
  },
  outcomes: {
    heading: "With Odingard, you can",
    items: [
      {
        title: "Keep process knowledge inside the boundary",
        body: "Cerberus judges the outbound action, so an agent can read the specification without being able to send it onward.",
      },
      {
        title: "Prove the control to a customer audit",
        body: "TraceLock records evidence write-once and crosswalks it to the frameworks your primes ask about.",
      },
    ],
  },
  drivers: {
    heading: "What buyers and regulators ask about",
    items: [
      {
        title: "IEC 62443",
        body: "The reference standard for industrial automation and control system security, increasingly cited in customer requirements.",
      },
      {
        title: "CMMC and NIST SP 800-171",
        body: "Defense suppliers handling controlled unclassified information inherit these through contract flow-down.",
      },
      {
        title: "Export control (ITAR / EAR)",
        body: "Technical data leaving the boundary can be a violation regardless of whether it was intentional.",
      },
    ],
  },
  products: {
    heading: "What delivers it",
    slugs: ["cerberus", "tracelock"],
  },
  services: {
    heading: "Bring in Warden",
    body:
      "Warden assesses where AI already touches production data and constrains the boundaries before an incident defines them for you.",
    links: [
      { label: "Agent Readiness Assessment", href: "/warden/agent-readiness" },
      { label: "Tool-Boundary Review", href: "/warden/tool-boundary-review" },
      { label: "Implementation & Migration", href: "/warden/implementation" },
    ],
  },
  resources: {
    heading: "Go deeper",
    items: [
      { kind: "Solution", label: "Secure Agentic AI", href: "/secure-agentic-ai" },
      { kind: "Solution", label: "Manage Third-Party Risk", href: "/third-party-risk" },
      { kind: "Product", label: "Cerberus", href: "/cerberus" },
    ],
  },
  close: {
    heading: "Use AI on production data without exporting it",
    body:
      "The constraint belongs at the action, not at the door. Start with a boundary review.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=manufacturing" },
      { label: "Explore Warden", href: "/warden" },
    ],
  },
};

const PUBLIC_SECTOR: OutcomePageContent = {
  path: "/industry/public-sector",
  kind: "industry",
  eyebrow: "Industry",
  headline:
    "Federal AI policy asks agencies to inventory, classify and safeguard AI use — and to show their work",
  lede:
    "Public-sector programs have to document AI use cases, manage the risk of the ones that affect rights or safety, and show the evidence on demand.",
  ctas: [
    { label: "Request a briefing", href: "/contact?topic=public-sector" },
    { label: "Explore TraceLock", href: "/tracelock" },
  ],
  challenge: {
    heading: "Governance to a published standard",
    body:
      "Agencies and the contractors serving them have to govern AI to a published standard, and prove it to program offices and assessors who expect evidence rather than intent.",
    points: [
      {
        title: "Inventory before classification",
        body: "Use-case inventories and risk determinations both assume you can enumerate what is running.",
      },
      {
        title: "Flow-down is not optional",
        body: "Contractors inherit the agency's security and AI requirements whether or not their tooling supports them.",
      },
      {
        title: "Assessors want artifacts",
        body: "A control described in a plan but not evidenced in the environment does not pass.",
      },
    ],
  },
  outcomes: {
    heading: "With Odingard, you can",
    items: [
      {
        title: "Maintain the AI inventory",
        body: "TraceLock discovers AI systems and holds them against the obligations that apply, rather than a spreadsheet refreshed annually.",
      },
      {
        title: "Constrain agents handling sensitive data",
        body: "Cerberus enforces the boundary at the tool call and produces a record of what was blocked and why.",
      },
      {
        title: "Keep evidence current against the baseline",
        body: "TraceLock tests controls continuously against NIST SP 800-53 and SP 800-171 and records the result write-once.",
      },
    ],
  },
  drivers: {
    heading: "The instruments in play",
    lede:
      "Odingard does not currently hold a FedRAMP authorization. Where a program requires one, deploy the self-hosted Cerberus runtime inside your own authorized boundary and treat the hosted products as out of scope until that changes.",
    items: [
      {
        title: "OMB M-24-10",
        body: "Agency AI governance, innovation and risk management — including use-case inventories and minimum practices for rights- and safety-impacting AI.",
      },
      {
        title: "NIST SP 800-53 and SP 800-171",
        body: "The control baselines behind agency authorizations and the contractor flow-down for controlled unclassified information.",
      },
      {
        title: "CMMC",
        body: "Assessed cybersecurity maturity for the defense industrial base, tied to contract eligibility.",
      },
      {
        title: "Section 508",
        body: "Accessibility obligations that apply to what you deliver, not only to what you buy.",
      },
    ],
  },
  products: {
    heading: "What delivers it",
    slugs: ["tracelock", "cerberus"],
  },
  services: {
    heading: "Bring in Warden",
    body:
      "Warden maps the AI estate to the applicable minimum practices, builds the crosswalk, and prepares the briefing material the program office will ask for.",
    links: [
      { label: "AI Inventory & Discovery", href: "/warden/ai-inventory" },
      { label: "NIST AI RMF Alignment", href: "/warden/nist-ai-rmf" },
      { label: "Board & Regulator Briefings", href: "/warden/briefings" },
      { label: "Implementation & Migration", href: "/warden/implementation" },
    ],
  },
  resources: {
    heading: "Go deeper",
    items: [
      { kind: "Solution", label: "Prove AI Compliance", href: "/ai-compliance" },
      { kind: "Solution", label: "Secure Agentic AI", href: "/secure-agentic-ai" },
      { kind: "Product", label: "TraceLock", href: "/tracelock" },
    ],
  },
  close: {
    heading: "Govern the AI, and show the work",
    body:
      "Inventory, enforcement and evidence run on one record. Start with whichever has the nearer deadline.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=public-sector" },
      { label: "Explore Warden", href: "/warden" },
    ],
  },
};

const UTILITIES: OutcomePageContent = {
  path: "/industry/utilities",
  kind: "industry",
  eyebrow: "Industry",
  headline: "Where downtime is a safety event, autonomy has to be provably bounded",
  lede:
    "Operators adopting AI for planning, maintenance and response need the assurance that an autonomous system cannot act outside the envelope it was given.",
  ctas: [
    { label: "Request a briefing", href: "/contact?topic=utilities" },
    { label: "Explore Cerberus", href: "/cerberus" },
  ],
  challenge: {
    heading: "The regulator asks what the system is permitted to do",
    body:
      "Critical infrastructure oversight is built around demonstrable boundaries and change control. An agent that decides its own next step is difficult to describe in those terms unless the boundary is enforced in the runtime.",
    points: [
      {
        title: "Assurance by policy is not assurance",
        body: "A documented restriction that nothing enforces will not survive an audit of an autonomous system.",
      },
      {
        title: "IT and OT converge at the assistant",
        body: "Operational context reaches AI tooling through the corporate side, outside the OT control set.",
      },
      {
        title: "Evidence has to be contemporaneous",
        body: "Reconstructing what an agent did after the fact is far harder than recording it as it happens.",
      },
    ],
  },
  outcomes: {
    heading: "With Odingard, you can",
    items: [
      {
        title: "Enforce the envelope in the runtime",
        body: "Cerberus blocks guarded outbound calls when the conditions for an unsafe action are met, and fails closed if its own pipeline is unavailable.",
      },
      {
        title: "Test the boundary adversarially",
        body: "Argus red-teams the agent estate so the failure mode is found in an exercise rather than in operations.",
      },
      {
        title: "Produce contemporaneous evidence",
        body: "Verdicts and control results are recorded as they happen, write-once, with digests an auditor can verify.",
      },
      {
        title: "Keep procedures current",
        body: "Policy and SOP authoring keeps operating procedures versioned and acknowledged rather than circulating as attachments.",
      },
    ],
  },
  drivers: {
    heading: "What oversight is built on",
    items: [
      {
        title: "NERC CIP",
        body: "Reliability standards for bulk electric system cyber security, audited against documented controls.",
      },
      {
        title: "IEC 62443",
        body: "Security for industrial automation and control systems, including zones, conduits and change management.",
      },
      {
        title: "NIST CSF 2.0",
        body: "The framework most commonly used to structure a critical-infrastructure program end to end.",
      },
    ],
  },
  products: {
    heading: "What delivers it",
    slugs: ["cerberus", "argus", "tracelock"],
  },
  services: {
    heading: "Bring in Warden",
    body:
      "Warden defines the envelope with your operations team, red-teams it, and deploys the runtime that enforces it.",
    links: [
      { label: "Tool-Boundary Review", href: "/warden/tool-boundary-review" },
      { label: "Agentic Red Teaming", href: "/warden/agentic-red-teaming" },
      { label: "Cerberus Runtime Deployment", href: "/warden/cerberus-deployment" },
    ],
  },
  resources: {
    heading: "Go deeper",
    items: [
      { kind: "Solution", label: "Secure Agentic AI", href: "/secure-agentic-ai" },
      { kind: "Product", label: "Argus — adversarial assessment", href: "/argus" },
      { kind: "Research", label: "Lethal Trifecta validation report", href: "/research" },
    ],
  },
  close: {
    heading: "Bound the autonomy before you extend it",
    body:
      "A boundary that is enforced in the runtime is something you can show an auditor. A policy document is not.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=utilities" },
      { label: "Explore Cerberus", href: "/cerberus" },
    ],
  },
};

const SMB: OutcomePageContent = {
  path: "/industry/smb",
  kind: "industry",
  eyebrow: "Industry",
  headline:
    "Your customers' security questionnaires assume you have a compliance team",
  lede:
    "Growing companies get held to enterprise expectations without the headcount to meet them. The way through is automation and evidence you only have to produce once.",
  ctas: [
    { label: "Explore TraceLock", href: "/tracelock" },
    { label: "Request a briefing", href: "/contact?topic=smb" },
  ],
  challenge: {
    heading: "Compliance is a sales blocker before it is a risk problem",
    body:
      "A deal stalls on a questionnaire, an insurer asks for controls you have not documented, and the work lands on whoever has the least to do that week.",
    points: [
      {
        title: "No dedicated owner",
        body: "Security and compliance are a part-time responsibility for someone whose day job is something else.",
      },
      {
        title: "Every buyer asks differently",
        body: "The same evidence gets reformatted for each customer, insurer and partner.",
      },
      {
        title: "AI adoption outpaced the policy",
        body: "The team started using AI tools long before anyone wrote down what is allowed.",
      },
    ],
  },
  outcomes: {
    heading: "With Odingard, you can",
    items: [
      {
        title: "Get to a first audit without hiring",
        body: "One control library, live connectors and standing evidence replace the consultant-and-spreadsheet approach.",
      },
      {
        title: "Answer questionnaires from a library",
        body: "Publish posture once and point buyers at it, instead of writing the same answers again.",
      },
      {
        title: "Give AI agents access without the breach",
        body: "Cerberus blocks dangerous agent actions at the tool boundary, so adopting AI does not mean accepting the exposure.",
      },
    ],
  },
  products: {
    heading: "What delivers it",
    slugs: ["tracelock", "cerberus"],
  },
  services: {
    heading: "Bring in Warden when you need the hours",
    body:
      "Warden runs the compliance program as a managed service, so the work happens without a permanent hire.",
    links: [
      { label: "Managed Compliance", href: "/warden/managed-compliance" },
      { label: "Policy Program Design", href: "/warden/policy-program" },
      { label: "Enablement & Training", href: "/warden/enablement" },
    ],
  },
  resources: {
    heading: "Go deeper",
    items: [
      { kind: "Solution", label: "Stay Continuously Audit-Ready", href: "/audit-ready" },
      { kind: "Product", label: "Cerberus", href: "/cerberus" },
      { kind: "Product", label: "TraceLock", href: "/tracelock" },
    ],
  },
  close: {
    heading: "Enterprise answers without an enterprise team",
    body:
      "Start with the control library and the evidence. The questionnaires get easier immediately.",
    ctas: [
      { label: "Explore TraceLock", href: "/tracelock" },
      { label: "Request a briefing", href: "/contact?topic=smb" },
    ],
  },
};

export const INDUSTRY_PAGES: OutcomePageContent[] = [
  FINANCIAL_SERVICES,
  HEALTHCARE,
  MANUFACTURING,
  PUBLIC_SECTOR,
  UTILITIES,
  SMB,
];
